// Read-only renderer for one website-builder section (shared by the builder and the gallery).
export function SiteSectionView({ s, theme = {}, navLinks = [], gallery = [], videos = [] }) {
  const accent = theme.accent || "#2563EB";
  const fg = theme.text || "#0F172A";
  const img = (id) => (gallery.find((g) => g.id === id) || {}).url;

  switch (s.type) {
    case "nav":
      return (
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: `${accent}55` }}>
          <span className="font-bold" style={{ color: accent }}>{s.heading || "My Site"}</span>
          <div className="flex gap-3 text-xs">{navLinks.map((l, i) => <span key={i} style={{ color: fg, opacity: 0.8 }}>{l}</span>)}</div>
        </div>
      );
    case "hero":
      return (
        <div className="px-6 py-10 text-center">
          <h2 className="text-3xl font-bold" style={{ color: fg }}>{s.heading || "Your headline goes here"}</h2>
          <p className="mt-2 text-sm" style={{ color: fg, opacity: 0.8 }}>{s.body || "One line that explains the page."}</p>
        </div>
      );
    case "text":
      return (
        <div className="px-6 py-6">
          <h3 className="text-lg font-semibold" style={{ color: accent }}>{s.heading || "Section title"}</h3>
          <p className="mt-1.5 text-sm" style={{ color: fg, opacity: 0.85 }}>{s.body || "Add your text…"}</p>
        </div>
      );
    case "cards":
      return (
        <div className="px-6 py-6">
          <h3 className="text-lg font-semibold mb-3" style={{ color: accent }}>{s.heading || "Features"}</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            {(s.cards || []).map((c, i) => (
              <div key={i} className="rounded-lg p-3 border" style={{ borderColor: `${accent}44` }}>
                <p className="text-sm font-semibold" style={{ color: fg }}>{c.title || `Card ${i + 1}`}</p>
                <p className="text-xs mt-1" style={{ color: fg, opacity: 0.75 }}>{c.body || "One short sentence"}</p>
              </div>
            ))}
          </div>
        </div>
      );
    case "image":
      return (
        <div className="px-6 py-6">
          {img(s.imageId) ? <img src={img(s.imageId)} alt={s.alt || ""} className="w-full h-48 object-cover rounded-lg" />
            : <div className="w-full h-40 rounded-lg border-2 border-dashed flex items-center justify-center text-xs" style={{ borderColor: `${accent}66`, color: fg }}>Choose a picture</div>}
          <p className="text-xs mt-2" style={{ color: fg, opacity: 0.8 }}>{s.heading}</p>
        </div>
      );
    case "gallery":
      return (
        <div className="px-6 py-6">
          <h3 className="text-lg font-semibold mb-3" style={{ color: accent }}>{s.heading || "Gallery"}</h3>
          <div className="grid grid-cols-3 gap-2">
            {[0, 1, 2].map((i) => (img(s.imageId)
              ? <img key={i} src={img(s.imageId)} alt={s.alt || ""} className="w-full h-24 object-cover rounded" />
              : <div key={i} className="h-24 rounded border-2 border-dashed" style={{ borderColor: `${accent}55` }} />))}
          </div>
        </div>
      );
    case "video": {
      const v = videos.find((x) => x.id === s.videoId);
      return (
        <div className="px-6 py-6">
          <div className="relative w-full h-48 rounded-lg overflow-hidden bg-black">
            {v && <img src={v.poster} alt={v.label} className="w-full h-full object-cover opacity-80" />}
            <div className="absolute inset-0 flex items-center justify-center text-white text-sm">{v ? `▶ ${v.label}` : "Choose a clip"}</div>
          </div>
          <p className="text-xs mt-2" style={{ color: fg, opacity: 0.8 }}>{s.body || "Say why this video helps."}</p>
        </div>
      );
    }
    case "cta":
      return (
        <div className="px-6 py-8 text-center">
          <h3 className="text-xl font-bold" style={{ color: fg }}>{s.heading || "Ready to start?"}</h3>
          <span className="inline-block mt-3 px-4 py-2 rounded-full text-sm font-semibold" style={{ background: accent, color: "#04121f" }}>{s.buttonText || "Button text"}</span>
        </div>
      );
    case "career":
      return (
        <div className="px-6 py-6">
          <h3 className="text-lg font-semibold" style={{ color: accent }}>{s.careerName || "Choose a career"}</h3>
          <div className="grid sm:grid-cols-3 gap-2 mt-2 text-xs" style={{ color: fg }}>
            <p><b>Wages:</b> {s.wage || "—"}</p>
            <p><b>Education:</b> {s.education || "—"}</p>
            <p><b>Skills:</b> {s.skills || "—"}</p>
          </div>
          <p className="text-xs mt-2" style={{ color: fg, opacity: 0.8 }}>{s.body || "How do design skills matter in another career cluster?"}</p>
        </div>
      );
    default:
      return <div className="px-6 py-4 text-xs border-t" style={{ borderColor: `${accent}44`, color: fg, opacity: 0.8 }}>{s.body || "Footer text"}</div>;
  }
}

// Full read-only page (used by the gallery card preview).
export default function SitePreview({ doc, config = {}, className = "" }) {
  const site = doc.site || {};
  const theme = site.theme || {};
  return (
    <div className={className} style={{ background: theme.bg || "#FFFFFF", color: theme.text || "#0F172A", fontFamily: theme.font || "Arial" }}>
      {(doc.sections || []).map((s) => (
        <SiteSectionView key={s.id} s={s} theme={theme} navLinks={site.navLinks || []} gallery={config.gallery || []} videos={config.videos || []} />
      ))}
    </div>
  );
}
